import React, {useEffect} from "react";
import CardSurface from "./components/CardSurface";
import FiltrationForm from "./components/FiltrationForm";
import MapPlaceholder from "./components/MapPlaceholder";
import Preloader from "./components/Preloader";
import handleWPAjaxRequest from "./functions/handleWPAjaxRequest";
import {BljStoreLocatorConfig} from "./types/BljStoreLocatorConfig";
import WPResponse from "./types/WPResponse";
import {BljStoreLocatorSettings} from "./types/BljStoreLocatorSettings";
import {APIProvider, Map} from "@vis.gl/react-google-maps";
import MarkerWithInfoWindow from "./components/MarkerWithInfoWindow";
import {GoogleMapsCircle} from "./components/GoogleMapsCircle";
import InfoWindowContent from "./components/InfoWindowContent";
import SearchResults from "./components/SearchResults";
import {useErrorBoundary} from "react-error-boundary";
import StoreLocatorRoot from "./components/StoreLocatorRoot";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "./store";
import {storeLocatorSettingsSliceActions} from "./store/storeLocatorSettingsSlice";

declare const bljStoreLocatorConfig: BljStoreLocatorConfig;

const StoreLocatorApp: React.FC = () => {

    const [isAppLoading, setIsAppLoading] = React.useState(true);

    const dispatch = useDispatch<AppDispatch>();

    const isSearchFormLoading = useSelector((state: RootState) => state.isSearchFormLoadingSlice);
    const storeLocatorSettings = useSelector((state: RootState) => state.storeLocatorSettingsSlice);
    const isSearchOccurred = useSelector((state: RootState) => state.isSearchOccurredSlice);
    const storeSearchCenterCoords = useSelector((state: RootState) => state.storeSearchCircleSlice.storeSearchCenterCoords);
    const searchCircleRadiusInMiles = useSelector((state: RootState) => state.storeSearchCircleSlice.searchCircleRadiusInMiles);
    const searchResults = useSelector((state: RootState) => state.searchResultsSlice);

    const {showBoundary} = useErrorBoundary<Error>();

    const generateRightColContent = () => {

        if (isSearchFormLoading) {
            return (
                <MapPlaceholder
                    captionText="Searching for stores..."
                />
            );
        }

        if (!isSearchOccurred) {
            return (
                <MapPlaceholder
                    captionText={
                        <>
                            Let’s retrieve some tasty food and treats for your buddy!<br/>
                            Fill out the search form to find nearby Bil-Jac products.
                        </>
                    }
                />
            );
        }

        if (searchResults.length === 0) {
            return (
                <MapPlaceholder
                    captionText={
                        <>
                            There are no stores found at the chosen place and within the selected search radius and the search criteria.<br/>
                            Please try a different search.
                        </>
                    }
                />
            );
        }

        if (
            searchCircleRadiusInMiles &&
            storeLocatorSettings.googleMapsSettings.googleMapsMapID.length > 0 &&
            storeSearchCenterCoords
        ) {
            return (
                <Map
                    defaultZoom={storeLocatorSettings.googleMapsSettings.initialZoomLevel}
                    defaultCenter={storeSearchCenterCoords}
                    id="blj-store-locator-map"
                    mapId={storeLocatorSettings.googleMapsSettings.googleMapsMapID}
                >
                    {
                        searchResults.map((storeSearchResultItem, index) => {
                            return <MarkerWithInfoWindow
                                key={index}
                                position={{
                                    lat: storeSearchResultItem.store.storeLocation.lat,
                                    lng: storeSearchResultItem.store.storeLocation.lng,
                                }}
                                infoWindowContent={
                                    <InfoWindowContent
                                        storeName={storeSearchResultItem.store.storeName}
                                        storeLocation={storeSearchResultItem.store.storeLocation}
                                        storePhone={storeSearchResultItem.store.storePhoneNumber}
                                        storeProductTypes={storeSearchResultItem.store.storeProductTypes}
                                        storeSearchDistanceInMiles={storeSearchResultItem.distanceInMiles}
                                    />
                                }
                            />
                        })
                    }

                    <GoogleMapsCircle
                        center={storeSearchCenterCoords}
                        radius={searchCircleRadiusInMiles * 1609.34} // Convert miles to meters
                        fillColor="#297eab"
                        fillOpacity={0.2}
                        strokeWeight={0}
                    />
                </Map>
            );
        }

        return null;

    }

    // Loading Settings of the App
    useEffect(() => {

        const requestBody = new FormData();
        requestBody.append("action", "blj_get_plugin_settings");
        requestBody.append('plugin_settings_nonce', bljStoreLocatorConfig.bljStoreLocatorSettingsNonce);

        handleWPAjaxRequest(requestBody)
            .then((response) => {
                return response.json();
            })
            .then((responseData: WPResponse<BljStoreLocatorSettings>) => {
                setIsAppLoading(false);

                if (responseData.success) {

                    dispatch(storeLocatorSettingsSliceActions.setStoreLocatorSettings(responseData.data));

                } else {

                    // Showing the error message to the user
                    throw new Error(responseData.data.message);

                }

            })
            .catch((error: Error) => {

                setIsAppLoading(false);
                console.error(error);

                // Setting the App Crashed State - Showing the Error Boundary
                showBoundary(error);

            })

    }, []);

    return (
        isAppLoading ? (
            <Preloader/>
        ) : (
            <APIProvider
                apiKey={storeLocatorSettings.googleMapsSettings.apiKey}
                libraries={[
                    'marker'
                ]}
                onError={(error) => {
                    showBoundary(error as Error);
                }}
            >
                <StoreLocatorRoot>

                    <div className="blj-store-locator-main">

                        <div className="blj-store-locator-left-col">
                            <CardSurface>
                                <FiltrationForm/>
                            </CardSurface>
                        </div>

                        <div className="blj-store-locator-right-col">
                            {generateRightColContent()}
                        </div>

                    </div>

                    {isSearchOccurred && (
                        <SearchResults
                            searchResults={searchResults}
                        />
                    )}

                </StoreLocatorRoot>
            </APIProvider>
        )
    );
}

export default StoreLocatorApp;